import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { WIN_TOAST_PUSH_EVENT } from '@core/constants/win-toast.constants';
import {
  DEV_MOCK_SELF_STEAMID,
  DEV_MOCK_WIN_TOAST_PUSH_EVENT,
  IS_SKINS_DEV_TOOLS_HIDDEN,
} from '@core/dev/dev-mock-skins';
import { ModalService } from '@core/modals/services/modal.service';
import { SessionService } from '@core/services/session.service';
import { SocketService } from '@core/services/socket.service';
import { classifyOfferSent } from './win-toast-push.port';
import { WinToastService } from './win-toast.service';

/**
 * Routes the BE's `offer_sent` push. Started once by the global
 * `WinToastContainerComponent`; lives for the whole app.
 *
 *  - Game offer → `WinToastService.show()` (the "Accept winnings" toast).
 *  - `game: "Store"` (store withdrawal) → `WithdrawSentModal`, opened by its
 *    registry key so the games feature does not import the wallet component.
 *    This replaces the retired store-withdrawal listener in the wallet feature.
 *  - Pushes for another `userid` (e.g. bot winners) are dropped by the port.
 *
 *  - Real path: `WIN_TOAST_PUSH_EVENT` on the socket (not subscribed while the
 *    constant is `null`).
 *  - Dev path (`IS_SKINS_DEV_TOOLS_HIDDEN === false`): the `/dev/skins`
 *    launcher dispatches `dev:win_toast` to this socket's local listeners with
 *    a BE-shaped payload, so the exact same handler runs. Those toasts are
 *    flagged `devMock` so a dev-raised toast stays distinguishable; when the
 *    dev session is logged out, `DEV_MOCK_SELF_STEAMID` stands in for the
 *    session steamid so the recipient guard still runs.
 */
@Injectable({ providedIn: 'root' })
export class WinToastPushListener {
  private readonly socketService = inject(SocketService);
  private readonly sessionService = inject(SessionService);
  private readonly modalService = inject(ModalService);
  private readonly winToastService = inject(WinToastService);
  private readonly destroyRef = inject(DestroyRef);

  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;

    if (WIN_TOAST_PUSH_EVENT !== null) {
      this.listen(WIN_TOAST_PUSH_EVENT, false);
    }
    if (!IS_SKINS_DEV_TOOLS_HIDDEN) {
      this.listen(DEV_MOCK_WIN_TOAST_PUSH_EVENT, true);
    }
  }

  private listen(event: string, devMock: boolean): void {
    fromEvent<unknown>(this.socketService.socket, event)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => this.handlePush(payload, devMock));
  }

  /** One entry point for both paths — keep all payload logic in the port. */
  handlePush(payload: unknown, devMock = false): void {
    const self = this.sessionService.profile()?.steamid ?? (devMock ? DEV_MOCK_SELF_STEAMID : null);
    const result = classifyOfferSent(payload, self, { devMock });
    if (!result) return;

    switch (result.kind) {
      case 'toast':
        this.winToastService.show(result.input);
        break;
      case 'store':
        this.modalService.openModal('WithdrawSentModal', result.data);
        break;
    }
  }
}
